import React, { useContext, useMemo } from 'react';
import { Divider, Avatar, Space } from 'antd';
import { ConfigContext } from '../../context/Config';
import Text from '../Text/Text';
import Link from '../Link';
import isEmpty from 'lodash/isEmpty';
import TimeViewer from '../TimeViewer/TimeViewer';
import { randomHex } from '../../utils/helper';
import findLastIndex from 'lodash/findLastIndex';
import min from 'lodash/min';
import Wrapper from '../Wrapper';
import Button from '../Button';
import IconAvailabilityCalendar from '../Icons/IconAvailabilityCalendar';
import IconLessonCalendar from '../Icons/IconLessonCalendar';
import IconAvailabilityClock from '../Icons/IconAvailabilityClock';
import './CalendarEventViewItem.less';

export type Attendee = {
  id?: string;
  name: string;
  avatar?: string;
  active?: boolean;
};

export type Event = {
  id?: string;
  title: string;
  eventStartDate: string;
  formattedEventStartDate: string;
  eventEndDate: string;
  attendees: Attendee[];
  meetingLinks?: { [key: string]: string };
  altLink?: string;
  color?: string;
  type?: 'event' | 'booking' | 'lesson';
  viewTitle?: string;
  courseTitle?: string;
  isCalendarEvent: boolean;
};

export type CalendarEventViewItemProps = Event & {
  locale?: string;
  titleColor?: 'black';
  avatarTextColor?: 'black';
  onItemClick?: (item: any) => void;
  onBook?: (item: any) => void;
  onCancel?: (item: any) => void;
  onReschedule?: (item: any) => void;
};

const TCalendarEventViewItem: React.FC<CalendarEventViewItemProps> = ({
  title,
  eventStartDate,
  formattedEventStartDate,
  eventEndDate,
  attendees = [],
  meetingLinks,
  altLink,
  type = 'event',
  locale,
  titleColor,
  avatarTextColor,
  viewTitle,
  courseTitle = '',
  onItemClick,
  onBook,
  onCancel,
  onReschedule,
  isCalendarEvent,
}) => {
  const { translate } = useContext(ConfigContext);
  const randomColorHex = useMemo(randomHex, []);

  const link =
    !isEmpty(meetingLinks) &&
    !!Object.values(meetingLinks)[0] &&
    Object.values(meetingLinks)[0];

  const nameFirstLetters = (name = '') => {
    const arr = name
      .split(' ')
      .map((s) => s.trim())
      .filter(Boolean);

    while (arr.length > 2) {
      const minVal = min(arr.map((s) => s.length));
      arr.splice(
        findLastIndex(arr, (s) => s.length === minVal),
        1,
      );
    }

    return arr.map((s) => s.charAt(0).toLocaleUpperCase()).join('');
  };

  const showTeachersGroup = () => {
    const currentAttendee = attendees.find(
      (attendee) => attendee.active === true,
    );
    return (
      <div className="t-avatarGroup" key={currentAttendee?.id}>
        <Avatar
          size="small"
          src={currentAttendee?.avatar || currentAttendee?.name}
          alt={currentAttendee?.name}
          icon={nameFirstLetters(currentAttendee?.name)}
          style={{ background: randomColorHex, fontSize: 13 }}
        />
        <div className="t-avatar-text">
          <Text textColor={avatarTextColor} rows={1} level={3}>
            {currentAttendee?.name}
          </Text>
        </div>
      </div>
    );
  };

  const showTeacher = () => {
    return (
      <div className="t-avatarGroup" key={attendees[0]?.id}>
        <Avatar
          size="small"
          src={attendees[0]?.avatar || attendees[0]?.name}
          alt={attendees[0]?.name}
          icon={nameFirstLetters(attendees[0]?.name)}
          style={{ background: randomColorHex, fontSize: 13 }}
        />
        <div className="t-avatar-text">
          <Text textColor={avatarTextColor} rows={1} level={3}>
            {attendees[0]?.name}
          </Text>
        </div>
      </div>
    );
  };

  const renderEventIcon = () => {
    if (!type) {
      return null;
    }

    let eventType;
    switch (type) {
      case 'booking':
        eventType = <IconAvailabilityCalendar size="md" />;
        break;
      default:
        eventType = <IconLessonCalendar size="md" />;
    }

    return eventType;
  };

  const renderBookingButtons = () => {
    if (isCalendarEvent) {
      return (
        <Wrapper>
          <Divider
            style={{ margin: '12px 0' }}
            type="horizontal"
            orientation="center"
          />
          <div className="t-bookingButton">
            <Button onClick={onBook}>{translate('ui.book')}</Button>
          </div>
        </Wrapper>
      );
    }
  };

  const renderLessonButtons = () => {
    if (isCalendarEvent) {
      return (
        <Wrapper>
          <Divider
            style={{ margin: '12px 0' }}
            type="horizontal"
            orientation="center"
          />
          <Wrapper
            mt={2}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            style={{ gap: 10 }}
          >
            <Button fullWidth onClick={onReschedule}>
              {translate('ui.reschedule')}
            </Button>
            <Button fullWidth onClick={onCancel} type={'default'}>
              {translate('ui.cancel')}
            </Button>
          </Wrapper>
        </Wrapper>
      );
    }
  };

  return (
    <div className="t-calendarListViewItem">
      <div className="t-infoContainer" onClick={onItemClick}>
        {/* Training title and icon part */}
        <Wrapper mt={2} mb={2}>
          <div className="t-titleAndImages">
            <span className="t-iconRowGap">{renderEventIcon()}</span>
            <div className="t-calendarEventTitle">
              <Text level={2} rows={1} semiBold>
                {viewTitle || title}
              </Text>
              {courseTitle && (
                <Text level={3} rows={1} textColor="dark-50">
                  {courseTitle}
                </Text>
              )}
            </div>
          </div>
        </Wrapper>

        {/* Event date part */}
        <Wrapper
          mt={2}
          mb={2}
          style={{ width: '100%' }}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Wrapper
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <span className={['t-iconClock t-iconRowGap'].join(' ')}>
              <IconAvailabilityClock size="xl" />
            </span>
            <Text level={3} rows={1}>
              {formattedEventStartDate}
            </Text>
          </Wrapper>

          <span className="t-eventStartEndDate">
            <TimeViewer
              locale={locale}
              start={eventStartDate}
              end={eventEndDate}
            />
          </span>
        </Wrapper>

        {/* Teacher name and avatar */}
        {attendees.length > 1 ? showTeachersGroup() : showTeacher()}
      </div>

      {type === 'booking' && renderBookingButtons()}

      {type === 'lesson' && renderLessonButtons()}
    </div>
  );
};

export default TCalendarEventViewItem;
